import React, { useState, useEffect } from 'react';
import { fetchProducts, fetchTanks, fetchPumps, savePumpReading, fetchPumpReadings, fetchTankReadings, fetchNozzles, saveNozzleReading, fetchNozzleReadings, saveTankReading, saveDipReading, fetchDipReadings, deleteTankReading, deletePumpReading, deleteNozzleReading, deleteDipReading } from '../../services/api';
import './Entry.css';

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  }
  return dateStr;
};

const formatDateForUI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length <= 2) {
    const [d, m, y] = dateStr.split("-");
    return `${y}-${m}-${d}`;
  }
  return dateStr;
};

const getTodayForUI = () => {
  const d = new Date();
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('-');
};

const initEntries = [];

export default function Entry({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [activeTab, setActiveTab] = useState('tank');
  const [entries, setEntries] = useState(initEntries);

  const [tanks, setTanks] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState({
    date: getTodayForUI(),
    product: '',
    tank: '',
    reading: '',
    openingReading: '',
    closingReading: '',
    shift: 'A',
    status: 'Active',
    stock: 'Opening'
  });

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  /* ---------------- FETCH TANK READINGS ---------------- */

  async function loadTankReadings() {
    try {
      const result = await fetchTankReadings({
        Companyid: companyId,
        FromDate: formatDateForAPI(form.date),
        ToDate: formatDateForAPI(form.date),

      });

      if (result.success && result.Response && Array.isArray(result.Response)) {
        const fetchEntries = result.Response.map(item => ({
          id: `tank_val_${item.Tankid}_${item.SNo}`,
          dbId: item.TRid,
          sno: item.SNo,
          tab: 'tank',
          product: item.ProductName,
          tank: item.TankName,
          reading: item.OpeningReading,
          openingReading: item.OpeningReading,
          closingReading: item.ClosingReading,
          openingId: item.OpeningTRid,
          closingId: item.ClosingTRid,
          balance: item.Balance,
          shift: item.Shift || 'A',
          date: item.Date,
          status: item.Status
        }));

        setEntries(prev => {
          const nonTankEntries = prev.filter(e => e.tab !== 'tank');
          return [...nonTankEntries, ...fetchEntries];
        });
      }

    } catch (err) {
      console.error("Tank reading fetch error", err);
    }
  }

  /* ---------------- FETCH PUMP READINGS ---------------- */

  async function loadPumpReadings() {
    try {
      const result = await fetchPumpReadings({ Companyid: companyId });
      if (result.success && result.Response && Array.isArray(result.Response)) {
        const fetchEntries = result.Response.map(item => ({
          id: `pump_val_${item.Pumpid}_${item.SNo}`,
          dbId: item.SNo,
          sno: item.SNo,
          tab: 'pump',
          product: item.ProductName,
          tank: item.PumpName,
          reading: item.OpeningReading,
          openingReading: item.OpeningReading,
          closingReading: item.ClosingReading,
          openingId: item.OpeningPRid,
          closingId: item.ClosingPRid,
          balance: item.Balance,
          date: item.Date,
          status: item.Status
        }));
        setEntries(prev => {
          const nonPumpEntries = prev.filter(e => e.tab !== 'pump');
          return [...nonPumpEntries, ...fetchEntries];
        });
      }
    } catch (err) {
      console.error("Pump reading fetch error", err);
    }
  }

  /* ---------------- FETCH NOZZLE READINGS ---------------- */

  async function loadNozzleReadings() {
    try {
      const result = await fetchNozzleReadings(companyId);
      if (result.success && result.Response && Array.isArray(result.Response)) {
        const fetchEntries = result.Response.map(item => ({
          id: `nozzle_val_${item.Nozzleid}_${item.SNo}`,
          dbId: item.SNo,
          sno: item.SNo,
          tab: 'nozzle',
          product: item.ProductName,
          tank: item.NozzleName,
          reading: item.OpeningReading,
          openingReading: item.OpeningReading,
          closingReading: item.ClosingReading,
          openingId: item.OpeningNRid,
          closingId: item.ClosingNRid,
          balance: item.Balance,
          date: item.Date,
          status: item.Status
        }));

        setEntries(prev => {
          const nonNozzleEntries = prev.filter(e => e.tab !== 'nozzle');
          return [...nonNozzleEntries, ...fetchEntries];
        });
      }
    } catch (err) {
      console.error("Nozzle reading fetch error", err);
    }
  }

  /* ---------------- FETCH DIP READINGS ---------------- */

  async function loadDipReadings() {
    try {
      const result = await fetchDipReadings(companyId);
      if (result.success && result.Response && Array.isArray(result.Response)) {
        const fetchEntries = result.Response.map(item => ({
          id: `dip_val_${item.Dipid}_${item.SNo}`,
          dbId: item.Dipid,
          sno: item.SNo,
          tab: 'dip',
          product: item.ProductName,
          tank: item.TankName,
          reading: item.OpeningReading,
          openingReading: item.OpeningReading,
          shift: item.Shift,
          stock: item.Type,
          date: item.Date,
          status: item.Status
        }));

        setEntries(prev => {
          const nonDipEntries = prev.filter(e => e.tab !== 'dip');
          return [...nonDipEntries, ...fetchEntries];
        });
      }
    } catch (err) {
      console.error("Dip reading fetch error", err);
    }
  }

  /* ---------------- FETCH DATA ---------------- */

  async function loadInitialData() {
    // These now use the centralized api.js
    const [prodRes, tankRes, pumpRes, nozzleRes] = await Promise.all([
      fetchProducts(companyId),
      fetchTanks(companyId),
      fetchPumps(companyId),
      fetchNozzles(companyId)
    ]);

    if (prodRes.success) setProducts(prodRes.Response || []);
    if (tankRes.success) setTanks(tankRes.Response || []);
    if (pumpRes.success) setPumps(pumpRes.Response || []);
    if (nozzleRes.success) setNozzles(nozzleRes.Response || []);

    loadTankReadings();
    loadPumpReadings();
    loadNozzleReadings();
    loadDipReadings();
  }

  useEffect(() => {
    loadInitialData();
  }, [companyId]);

  /* ---------------- SAVE ENTRY ---------------- */

  const handleSave = async () => {

    if (form.openingReading === "" && form.closingReading === "" && form.reading === "") return;

    try {

      /* ---- CALL API ONLY FOR TANK TAB ---- */

      if (activeTab === "tank") {
        const payloadBase = {
          Companyid: companyId,
          TankName: form.tank,
          Product: form.product,
          Status: form.status,
          Date1: formatDateForAPI(form.date),
          Shift: form.shift,
        };

        let lastResult;
        // Save/Update Opening
        if (form.openingReading !== "") {
          const openingPayload = {
            ...payloadBase,
            Reading: form.openingReading,
            Type: "Opening",
            IDS: form.openingId ? String(form.openingId) : "0",
            TRid: form.openingId ? String(form.openingId) : "0"
          };
          lastResult = await saveTankReading(openingPayload);
        }

        // Save/Update Closing
        if (form.closingReading !== "") {
          const closingPayload = {
            ...payloadBase,
            Reading: form.closingReading,
            Type: "Closing",
            IDS: form.closingId ? String(form.closingId) : "0",
            TRid: form.closingId ? String(form.closingId) : "0"
          };
          lastResult = await saveTankReading(closingPayload);
        }

        if (lastResult?.success || lastResult?.Response === "UPDATED" || lastResult?.Response === "INSERTED" || lastResult?.Response === "ADDED") {
          await loadTankReadings();
          setEditId(null);
          setForm({ ...form, reading: "", openingReading: "", closingReading: "", openingId: 0, closingId: 0 });
        } else {
          alert(lastResult?.message || "Tank entry failed");
          return;
        }

      } else if (activeTab === "pump") {
        const payloadBase = {
          Companyid: companyId,
          PumpName: form.tank,
          Product: form.product,
          Status: form.status,
          Date1: formatDateForAPI(form.date),
          Shift: form.shift,
        };

        let lastResult;
        if (form.openingReading !== "") {
          lastResult = await savePumpReading({
            ...payloadBase,
            Reading: form.openingReading,
            Type: "Opening",
            IDS: form.openingId ? String(form.openingId) : "0",
            PRid: form.openingId ? String(form.openingId) : "0"
          });
        }
        if (form.closingReading !== "") {
          lastResult = await savePumpReading({
            ...payloadBase,
            Reading: form.closingReading,
            Type: "Closing",
            IDS: form.closingId ? String(form.closingId) : "0",
            PRid: form.closingId ? String(form.closingId) : "0"
          });
        }

        if (lastResult?.success || lastResult?.Response === "UPDATED" || lastResult?.Response === "INSERTED" || lastResult?.Response === "ADDED") {
          await loadPumpReadings();
          setEditId(null);
          setForm({ ...form, reading: "", openingReading: "", closingReading: "", openingId: 0, closingId: 0 });
        } else {
          alert(lastResult?.message || "Pump entry failed");
          return;
        }

      } else if (activeTab === "nozzle") {
        const payloadBase = {
          Companyid: companyId,
          NozzleName: form.tank,
          Product: form.product,
          Status: form.status,
          Date1: formatDateForAPI(form.date),
          Shift: form.shift,
        };

        let lastResult;
        if (form.openingReading !== "") {
          lastResult = await saveNozzleReading({
            ...payloadBase,
            Reading: form.openingReading,
            Type: "Opening",
            IDS: form.openingId ? String(form.openingId) : "0",
            NRid: form.openingId ? String(form.openingId) : "0"
          });
        }
        if (form.closingReading !== "") {
          lastResult = await saveNozzleReading({
            ...payloadBase,
            Reading: form.closingReading,
            Type: "Closing",
            IDS: form.closingId ? String(form.closingId) : "0",
            NRid: form.closingId ? String(form.closingId) : "0"
          });
        }

        if (lastResult?.success || lastResult?.Response === "UPDATED" || lastResult?.Response === "INSERTED" || lastResult?.Response === "ADDED") {
          await loadNozzleReadings();
          setEditId(null);
          setForm({ ...form, reading: "", openingReading: "", closingReading: "", openingId: 0, closingId: 0 });
        } else {
          alert(lastResult?.message || "Nozzle entry failed");
          return;
        }

      } else if (activeTab === "dip") {

        const result = await saveDipReading({
          Companyid: companyId,
          TankName: form.tank,
          Product: form.product,
          Reading: form.reading,
          Type: "Opening",
          Status: form.status,
          Date1: formatDateForAPI(form.date),
          Shift: form.shift,
          IDS: editId ? String(editId) : "0",
          Dipid: editId ? String(editId) : "0"
        });

        if (result.success || result.Response === "UPDATED" || result.Response === "INSERTED" || result.Response === "ADDED") {
          await loadDipReadings();
          setEditId(null);
          setForm({ ...form, reading: "", openingReading: "", closingReading: "", openingId: 0, closingId: 0 });
        } else {
          alert(result.message || "Dip opening entry failed");
          return;
        }

      } else {

        /* ---- LOCAL TABLE UPDATE FOR NON-TANK ---- */

        setEntries(prev => [
          ...prev,
          {
            id: Date.now(),
            tab: activeTab,
            ...form
          }
        ]);

      }

      setShowForm(false);
      setEditId(null);

      setForm({
        date: getTodayForUI(),
        product: '',
        tank: '',
        reading: '',
        openingReading: '',
        closingReading: '',
        shift: 'A',
        status: 'Active',
        stock: 'Opening'
      });

    } catch (err) {
      console.error("Save error:", err);
      alert("Something went wrong");
    }
  };

  function handleEdit(entry) {
    setEditId(entry.sno || entry.dbId);
    setForm({
      date: formatDateForUI(entry.date),
      product: entry.product,
      tank: entry.tank,
      openingReading: entry.openingReading || entry.reading || '',
      closingReading: entry.closingReading || '',
      openingId: entry.openingId || 0,
      closingId: entry.closingId || 0,
      reading: entry.reading || '',
      shift: entry.shift || 'A',
      status: entry.status,
      stock: entry.stock || 'Opening'
    });
    setShowForm(true);
  }

  const handleDelete = async (entry) => {
    if (!window.confirm(`Are you sure you want to delete this ${activeTab} reading?`)) return;

    try {
      let result;
      if (activeTab === 'tank') {
        result = await deleteTankReading(entry.sno || entry.dbId);
      } else if (activeTab === 'pump') {
        result = await deletePumpReading(entry.sno || entry.dbId);
      } else if (activeTab === 'nozzle') {
        result = await deleteNozzleReading(entry.sno || entry.dbId);
      } else if (activeTab === 'dip') {
        result = await deleteDipReading(entry.sno || entry.dbId);
      }

      if (result && (result.success || result.Response === "DELETED" || result.status === "Success")) {
        // Re-fetch data for the active tab
        if (activeTab === 'tank') await loadTankReadings();
        else if (activeTab === 'pump') await loadPumpReadings();
        else if (activeTab === 'nozzle') await loadNozzleReadings();
        else if (activeTab === 'dip') await loadDipReadings();
      } else {
        alert(result?.message || "Failed to delete record");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Something went wrong while deleting");
    }
  };

  return (

    <div className="entry-page">

      <div className="page-header">

        <h2 className="page-title">Readings</h2>

        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setEditId(null);
            setForm({
              date: getTodayForUI(),
              product: '',
              tank: '',
              reading: '',
              openingReading: '',
              closingReading: '',
              shift: 'A',
              status: 'Active',
              stock: 'Opening'
            });
            setShowForm(true);
          }}
        >
          ＋ New Entry
        </button>

      </div>

      {/* TABS */}

      <div className="tabs">

        <button
          className={`tab-btn ${activeTab === 'tank' ? 'active' : ''}`}
          onClick={() => setActiveTab('tank')}
        >
          Tank
        </button>

        <button
          className={`tab-btn ${activeTab === 'pump' ? 'active' : ''}`}
          onClick={() => setActiveTab('pump')}
        >
          Pump
        </button>

        <button
          className={`tab-btn ${activeTab === 'nozzle' ? 'active' : ''}`}
          onClick={() => setActiveTab('nozzle')}
        >
          Nozzle
        </button>

        {/* <button
          className={`tab-btn ${activeTab === 'dip' ? 'active' : ''}`}
          onClick={() => setActiveTab('dip')}
        >
          Dip
        </button> */}

      </div>

      {/* FORM */}

      {showForm && (

        <div className="section-card">

          <div className="section-header">

            {activeTab === 'tank' && 'Tank Entry'}
            {activeTab === 'nozzle' && 'Nozzle Entry'}
            {activeTab === 'dip' && 'Dip Entry'}
            {activeTab === 'pump' && 'Pump Entry'}

          </div>

          <div className="form-row">

            {/* PRODUCT */}

            <div className="form-group">

              <label>Product</label>

              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >

                <option value="">Select Product</option>

                {products.map(p => (
                  <option key={p.Productid} value={p.Name}>
                    {p.Name}
                  </option>
                ))}

              </select>

            </div>

            {/* TANK / PUMP / NOZZLE */}

            <div className="form-group">

              <label>
                {activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}
              </label>

              <select
                className="form-control"
                value={form.tank}
                onChange={e => {
                  const val = e.target.value;
                  let prod = form.product;

                  if (activeTab === 'nozzle') {
                    const nz = nozzles.find(n => n.NozzleName === val);
                    if (nz) prod = nz.ProductName || nz.Product || prod;
                  } else if (activeTab === 'tank') {
                    const tk = tanks.find(t => t.TankName === val);
                    if (tk) prod = tk.ProductName || tk.Product || prod;
                  } else if (activeTab === 'pump') {
                    const pm = pumps.find(p => p.PumpName === val);
                    if (pm) prod = pm.ProductName || pm.Product || prod;
                  }

                  setForm({ ...form, tank: val, product: prod });
                }}
              >

                <option value="">
                  Select {activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}
                </option>

                {activeTab === 'pump' ? (
                  pumps.map(p => (
                    <option key={p.Pumpid} value={p.PumpName}>
                      {p.PumpName}
                    </option>
                  ))
                ) : activeTab === 'nozzle' ? (
                  nozzles.map(n => (
                    <option key={n.Nozzleid} value={n.NozzleName}>
                      {n.NozzleName}
                    </option>
                  ))
                ) : (
                  tanks.map(t => (
                    <option key={t.Tankid} value={t.TankName}>
                      {t.TankName}
                    </option>
                  ))
                )}

              </select>

            </div>

            {/* READING */}

            {/* READINGS (Opening & Closing) */}
            {activeTab !== 'dip' ? (
              <>
                <div className="form-group">
                  <label>Opening Reading</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.openingReading}
                    placeholder="Enter Opening"
                    onChange={e => setForm({ ...form, openingReading: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Closing Reading</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.closingReading}
                    placeholder="Enter Closing"
                    onChange={e => setForm({ ...form, closingReading: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>Reading</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.reading}
                  placeholder="Enter Reading"
                  onChange={e => setForm({ ...form, reading: e.target.value })}
                />
              </div>
            )}

            {/* SHIFT */}

            <div className="form-group">
              <label>Shift</label>

              <select
                className="form-control"
                value={form.shift}
                onChange={e => setForm({ ...form, shift: e.target.value })}
              >
                <option value="Select">Select</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
            </div>

            {/* DATE */}

            <div className="form-group">
              <label>Date</label>

              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            {/* STOCK */}

            <div className="form-group">
              <label>Stock</label>

              <select
                className="form-control"
                value={form.stock}
                onChange={e => setForm({ ...form, stock: e.target.value })}
              >
                <option value="">Select Stock Type</option>
                <option value="Opening">Opening</option>
                <option value="Closing">Closing</option>
                {/* <option value="Closing">Closing</option>
                <option value="Adjustment">Adjustment</option> */}
              </select>
            </div>

            {/* STATUS */}

            <div className="form-group">
              <label>Status</label>

              <select
                className="form-control"
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

          </div>

          <div className="form-row" style={{ paddingTop: 0 }}>

            <div style={{ flex: 1 }} />

            <div className="action-bar">

              <button
                className="btn btn-primary"
                onClick={handleSave}
              >
                <i className="bi bi-save me-1"></i>
                {editId ? "Update" : "Save"}
              </button>

              <button
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                ✕ Cancel
              </button>

            </div>

          </div>

        </div>

      )}

      {/* TABLE */}

      <div className="section-card">

        <div className="section-header">
          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Entries
        </div>

        <div className="table-wrapper">

          <table className="data-table">

            <thead>
              <tr>
                <th className="text-left">Product</th>
                <th className="text-left">
                  {activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}
                </th>
                {activeTab === 'tank' || activeTab === 'pump' || activeTab === 'nozzle' ? (<>
                  <th className="text-right">Opening Reading</th>
                  <th className="text-right">Closing Reading</th>
                  <th className="text-right">Balance</th>
                </>
                ) : (
                  <>
                    <th className="text-right">Reading</th>
                    {/* <th className="text-center">Shift</th>
                    <th className="text-center">Stock</th> */}
                  </>
                )}
                <th className="text-center">Date</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>

              {entries
                .filter(e => e.tab === activeTab)
                .map(e => (

                  <tr key={e.id}>
                    <td className="text-left">
                      <span className="badge badge-info">{e.product}</span>
                    </td>
                    <td className="text-left">{e.tank}</td>
                    {activeTab === 'tank' || activeTab === 'pump' || activeTab === 'nozzle' ? (<>
                      <td className="text-right">{parseFloat(e.openingReading || e.reading || 0).toFixed(3)}</td>
                      <td className="text-right">{e.closingReading ? parseFloat(e.closingReading).toFixed(3) : '-'}</td>
                      <td className="text-right" style={{ fontWeight: 700 }}>{e.balance ? parseFloat(e.balance).toFixed(3) : '-'}</td>
                    </>
                    ) : (
                      <>
                        <td className="text-right">{parseFloat(e.reading || 0).toFixed(3)}</td>
                        <td className="text-center">{e.shift}</td>
                        <td className="text-center">{e.stock}</td>
                      </>
                    )}
                    <td className="text-center">{e.date}</td>
                    <td className="text-center">
                      <span className={`badge ${e.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <button className="icon-btn" onClick={() => handleEdit(e)} title="Edit">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    </td>

                  </tr>

                ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}